import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { formatTimeAgo } from '@/datetime/timeAgo.utilities';

import { reloadWorksheet } from '@/workbench/workbench.utilities';
import {
  useCurrentWorkstepCreatedAt,
  useCurrentWorkstepId,
  useIsWorkstepError,
  useLastWorkstep,
  useNextWorkstep,
  usePreviousWorkstep,
} from '@/worksteps/worksteps.store';
import { getWorksteps, setCurrentWorkstep } from '@/worksteps/worksteps.utilities';
import { DirectionEnum } from '@seeqdev/api/model/WorkstepChronologyOutputV1';
import { Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import type { WorkstepChronologyEntryV1 } from '@seeqdev/api/model/WorkstepChronologyEntryV1';

export type WorkstepButtonType = 'previous' | 'next' | 'last';

interface WorkstepIconsProps {
  isViewMode: boolean;
  isEditMode: boolean;
}

export const WorkstepIcons = ({ isViewMode, isEditMode }: WorkstepIconsProps) => {
  const previous = usePreviousWorkstep();
  const next = useNextWorkstep();
  const last = useLastWorkstep();
  const currentId = useCurrentWorkstepId();
  const currentCreatedAt = useCurrentWorkstepCreatedAt();
  const isError = useIsWorkstepError();
  const previousAvailable = !_.isUndefined(previous);
  const nextAvailable = !_.isUndefined(next);
  const lastAvailable = !_.isUndefined(last) && last !== currentId;
  const { t } = useTranslation();

  const [beforeShown, setBeforeShown] = useState(false);
  const [afterShown, setAfterShown] = useState(false);
  const [chronology, setChronology] = useState<{ value: string; label: string }[]>();

  useEffect(() => {
    if (beforeShown || afterShown) {
      getWorksteps(
        sqWorkbenchStore.stateParams.workbookId,
        sqWorkbenchStore.stateParams.worksheetId,
        currentId ?? '',
        beforeShown ? DirectionEnum.Before : DirectionEnum.After,
      ).then((response) => {
        setChronology(mapChronology(response.entries, sqWorkbenchStore.userLanguage));
      });
    } else {
      setChronology(undefined);
    }
  }, [beforeShown, afterShown, currentId, currentCreatedAt]);

  /**
   * Navigates to the specified workstep.
   *
   * @param workstepId - The id of the workstep
   * @returns A promise that resolves when the workstep has been set as the current one
   */
  const goToWorkstep = async (workstepId: string): Promise<any> => {
    await setCurrentWorkstep(
      sqWorkbenchStore.stateParams.workbookId,
      sqWorkbenchStore.stateParams.worksheetId,
      workstepId,
    );
    reloadWorksheet();
  };

  return (
    <div
      className={isViewMode ? 'flexSelfStretch' : 'flexColumnContainer flexJustifyEnd ml10 mr1 flexNoGrowNoShrink'}
      data-testid="workstepIcons">
      {isEditMode &&
        !isViewMode &&
        !sqWorkbookStore.isTopic &&
        !sqWorkbookStore.isVantage &&
        !sqWorkbookStore.isActionBoard && (
          <div className="unselectable">
            {isError && (
              <Icon
                icon="fa-exclamation-triangle"
                extraClassNames="workstepButton fa-xlg pr10"
                type="danger"
                tooltip={t('WORKSTEPS.ERROR')}
                tooltipPlacement="bottom"
              />
            )}

            <Icon
              icon="fa-reply"
              extraClassNames={clsx(
                {
                  'workstepButton': previousAvailable,
                  'sq-icon-gray': !previousAvailable,
                },
                'fa-xlg pr2',
              )}
              type={previousAvailable ? 'theme-light' : 'gray'}
              onClick={() => {
                if (previousAvailable && previous) {
                  goToWorkstep(previous);
                }
              }}
              testId="previousWorkstep"
              tooltip={t('WORKSTEPS.PREVIOUS')}
              tooltipPlacement="bottom"
            />

            <TriggerWithDropdown
              disabled={!previousAvailable}
              onOpenChange={(show) => setBeforeShown(show || false)}
              extraClassNames="mr4"
              contentExtraClassNames="overflowYAuto max-height-400 workstepSelectItems"
              containerTestId="previousWorkstepPopover"
              triggerIcon={
                <div className="sq-icon-hover">
                  <Icon
                    testId="previousWorkstepPulldown"
                    icon="fa-caret-down"
                    extraClassNames={clsx(
                      {
                        'workstepButton': previousAvailable,
                        'sq-icon-gray': !previousAvailable,
                      },
                      'verticalAlignTextBottom pr10 noLeftRadius',
                    )}
                    type={previousAvailable ? 'theme-light' : 'gray'}
                  />
                </div>
              }
              dropdownItems={
                previousAvailable && beforeShown && chronology
                  ? chronology.map(({ value, label }) => ({
                      label,
                      onClick: () => {
                        setBeforeShown(false);
                        goToWorkstep(value);
                      },
                    }))
                  : []
              }
              tooltipPlacement="bottom"
            />

            <Icon
              icon="fa-share"
              extraClassNames={clsx(
                {
                  'workstepButton': nextAvailable,
                  'sq-icon-gray': !nextAvailable,
                },
                'fa-xlg',
              )}
              type={nextAvailable ? 'theme-light' : 'gray'}
              onClick={() => (nextAvailable ? goToWorkstep(next!) : null)}
              testId="nextWorkstep"
              tooltip={t('WORKSTEPS.NEXT')}
              tooltipPlacement="bottom"
            />

            <TriggerWithDropdown
              disabled={!nextAvailable}
              onOpenChange={(show) => setAfterShown(show || false)}
              extraClassNames="inlineBlock"
              contentExtraClassNames="overflowYAuto max-height-400 workstepSelectItems"
              triggerIcon={
                <div className="sq-icon-hover">
                  <Icon
                    testId="nextWorkstepPulldown"
                    icon="fa-caret-down"
                    extraClassNames={clsx(
                      {
                        'workstepButton': nextAvailable,
                        'sq-icon-gray': !nextAvailable,
                      },
                      'verticalAlignTextBottom pr10 noLeftRadius',
                    )}
                    type={nextAvailable ? 'theme-light' : 'gray'}
                  />
                </div>
              }
              placement="bottom"
              containerTestId="nextWorkstepPopover"
              dropdownItems={
                nextAvailable && afterShown && chronology
                  ? chronology.map(({ value, label }) => ({
                      label,
                      onClick: () => {
                        setAfterShown(false);
                        goToWorkstep(value);
                      },
                    }))
                  : []
              }
            />

            <Icon
              icon="fa-reply-all"
              extraClassNames={clsx(
                {
                  'workstepButton': lastAvailable,
                  'sq-icon-gray': !lastAvailable,
                },
                'fa-xlg pr3 pl9 fa-flip-horizontal',
              )}
              type={lastAvailable ? 'theme-light' : 'gray'}
              onClick={() => (lastAvailable ? goToWorkstep(last!) : null)}
              testId="lastWorkstep"
              tooltip={t('WORKSTEPS.LAST')}
              tooltipPlacement="bottom"
            />
          </div>
        )}
    </div>
  );
};

const mapChronology = (
  entries: WorkstepChronologyEntryV1[] | undefined,
  language: string,
  currentTime: number = Date.now(),
) => {
  // If there are no entries, return an empty array
  if (!entries) {
    return [];
  }

  // Deduplicate the entries to avoid showing too many options
  const minDeltaInMs = 5_000;
  const deduped: WorkstepChronologyEntryV1[] = [];
  let last: WorkstepChronologyEntryV1;
  entries.forEach((value, index) => {
    if (index === 0 || (value.createdAt || 0) <= (last.createdAt || 0) - minDeltaInMs) {
      deduped.push(value);
      last = value;
    }
  });

  // Create a relative time formatter
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'always', style: 'narrow' });

  // Map the entries to the options
  const mapping = (entry: WorkstepChronologyEntryV1) => ({
    value: entry.workstepId!,
    label: formatTimeAgo(rtf, Math.floor((currentTime - (entry.createdAt || 0)) / 1000), entry.createdAt || 0),
  });
  return deduped.map(mapping);
};

export const mapChronologyExposedForTestingOnly = mapChronology;
